import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '../../../../components/Sheet';
import { MoodBoardField } from '../../../creatives/components/StoryboardComposer/MoodBoardField';
import { MAX_MOOD_IMAGES, type ArtStyle } from '../../art-style-api';
import { ART_MORPH } from '../../useIngredientsMorph';
import type { UseArtStyle } from '../../useArtStyle';
import { DirectionField } from '../DirectionField';
import { IngredientSection, SectionHint } from '../IngredientSection';
import { MascotsField } from '../MascotsField';
import { Root, Lead, Stack, PreviewBody, PreviewImg } from './IngredientsBody.styles';

/** The Ingredients accordion's sections. */
export type IngredientKey = 'mood' | 'direction' | 'characters';

export type IngredientsBodyProps = {
  style: ArtStyle;
  /** The open style's uploads and edits (from useArtStyle). */
  art: Pick<
    UseArtStyle,
    | 'pending'
    | 'describingId'
    | 'nameReveals'
    | 'edit'
    | 'addImages'
    | 'removeImage'
    | 'addMascot'
    | 'removeMascot'
    | 'renameMascot'
    | 'suggestMascotName'
  >;
  /** The one open section (the page keeps it, so the sheet and the rail open the same one). */
  openSection: IngredientKey | null;
  onToggleSection: (key: IngredientKey) => void;
};

/**
 * What Lumio reads to build the style: the mood board, a written direction and the cast of characters, as an accordion
 * with one section open at a time. The rail shows it beside the samples, the sheet before the first extraction; it is
 * the same box in both, so the morph between them carries it across.
 */
export const IngredientsBody = ({
  style,
  art,
  openSection,
  onToggleSection,
}: IngredientsBodyProps): React.ReactElement => {
  const { t } = useTranslation();
  // A mood board or character image open full size, in its own Sheet stacked over the page (or over the
  // ingredients sheet).
  const [preview, setPreview] = React.useState<{ url: string; kind: 'mood' | 'character' } | null>(null);
  const { closeHandle: previewCloseHandle } = useSheetDismiss(() => setPreview(null));

  const moodPending = art.pending.filter((p) => p.kind === 'mood').map(({ key, dataUri }) => ({ key, dataUri }));
  // The Characters header while closed: the first two names and how many more.
  const castNames = style.mascots.map((mascot) => mascot.name.trim() || t('Unnamed character'));
  const charactersSummary =
    castNames.length === 0
      ? t('Optional')
      : castNames.length <= 2
        ? castNames.join(', ')
        : t('{{names}} +{{n}}', { names: castNames.slice(0, 2).join(', '), n: castNames.length - 2 });

  return (
    <Root data-art-morph={ART_MORPH.body}>
      <Lead text="What Lumio reads to build your style." tag="span" />
      <Stack>
        <IngredientSection
          id="ingredient-mood"
          title={t('Mood board')}
          icon="palette"
          summary={
            style.mood_images.length > 0
              ? t('{{n}} of {{max}}', { n: style.mood_images.length, max: MAX_MOOD_IMAGES })
              : t('Add images')
          }
          open={openSection === 'mood'}
          onToggle={() => onToggleSection('mood')}
        >
          <SectionHint text="Mood board images set the look: rendering, light, materials and colour." tag="span" />
          <MoodBoardField
            headless
            label={t('Mood board')}
            emptyHint={t('Add at least one to extract a style.')}
            removeLabel={t('Remove mood image')}
            addTitle={t('Add mood image')}
            addText={t('Add images')}
            showCount
            primaryAdd
            images={style.mood_images.map((image) => image.url)}
            pending={moodPending}
            max={MAX_MOOD_IMAGES}
            onAddFiles={(files) => art.addImages('mood', files)}
            onRemove={(index) => art.removeImage('mood', index)}
            onOpen={(index) => {
              const url = style.mood_images[index]?.url;
              if (url) setPreview({ url, kind: 'mood' });
            }}
            openLabel={t('Open mood board image')}
          />
        </IngredientSection>
        <IngredientSection
          id="ingredient-direction"
          title={t('Direction')}
          icon="vibe"
          optional
          summary={style.prompt.trim() || t('Optional')}
          open={openSection === 'direction'}
          onToggle={() => onToggleSection('direction')}
        >
          <DirectionField value={style.prompt} onChange={(prompt) => art.edit({ prompt })} />
        </IngredientSection>
        <IngredientSection
          id="ingredient-characters"
          title={t('Characters')}
          icon="user"
          summary={charactersSummary}
          open={openSection === 'characters'}
          onToggle={() => onToggleSection('characters')}
        >
          <MascotsField
            headless
            mascots={style.mascots}
            pending={art.pending}
            describingId={art.describingId}
            nameReveals={art.nameReveals}
            onAdd={() => void art.addMascot()}
            onRemove={art.removeMascot}
            onRename={art.renameMascot}
            onSuggestName={art.suggestMascotName}
            onAddFiles={(mascotId, files) => art.addImages('mascot', files, mascotId)}
            onRemoveImage={(mascotId, index) => art.removeImage('mascot', index, mascotId)}
            onOpenImage={(mascotId, index) => {
              const url = style.mascots.find((mascot) => mascot.id === mascotId)?.images[index]?.url;
              if (url) setPreview({ url, kind: 'character' });
            }}
          />
        </IngredientSection>
      </Stack>
      {preview ? (
        <Sheet
          title={preview.kind === 'character' ? 'Character image' : 'Mood board image'}
          ariaLabel={preview.kind === 'character' ? 'Character image' : 'Mood board image'}
          onClose={() => setPreview(null)}
          closeHandle={previewCloseHandle}
        >
          <PreviewBody>
            <PreviewImg
              src={preview.url}
              alt={preview.kind === 'character' ? t('Character image') : t('Mood board image')}
            />
          </PreviewBody>
        </Sheet>
      ) : null}
    </Root>
  );
};

export default IngredientsBody;
